/* =====================================================================
 * CAD Drawing API client — `/api/v1/cad/drawing/*` 의 thin wrapper.
 *
 * "도면 만들기" 화면은 자재관리와 같은 프로젝트/폴더/파일/채팅 UX 를 쓰지만
 * 산출물은 STEP/STL/SVG/PNG 등 CAD 생성 파일이다. 자재관리 API 와 같은
 * 함수 모양을 유지해 프론트 컴포넌트가 동일한 디자인 토큰을 재사용한다.
 * ===================================================================== */

import type { WorkspaceFile } from '@auw/workspace-utils';
import { authFetch, getHeaders as baseGetHeaders, API_BASE_URL } from './authFetch';
import {
  type CadMaterialMessage,
  extractCompletedContent,
} from './cadMaterial';

const DRAWING_PREFIX = `${API_BASE_URL}/api/v1/cad/drawing`;

function jsonHeaders(): Record<string, string> {
  return baseGetHeaders(true);
}

async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export interface CadDrawingProject {
  project_id: string;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CadDrawingFolder {
  folder_id: string;
  project_id: string;
  parent_folder_id: string | null;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CadDrawingUploadResponse {
  uploaded: WorkspaceFile[];
  count: number;
  errors: Array<{ file: string; error: string }> | null;
}

export type CadDrawingMessage = CadMaterialMessage;

export interface CadDrawingMessagesResponse {
  messages: CadDrawingMessage[];
  count: number;
}

export interface CadDrawingFileListOptions {
  folderId?: string | null;
  includeAllFolders?: boolean;
}

export interface CadDrawingArtifact {
  file_id: string;
  original_name: string;
  mime_type: string;
  artifact_role: 'assembly' | 'part' | 'drawing' | 'source' | 'manifest' | 'other';
  artifact_format: string;
  created_at?: string | null;
}

export interface CadDrawingGenerationResult {
  project_id: string;
  message_id?: string | null;
  status: 'completed' | 'failed';
  summary: string;
  artifacts: CadDrawingArtifact[];
  manifest?: Record<string, unknown> | null;
  error_message?: string | null;
}

export type CadDrawingChatFrameHandlers = {
  onTextDelta?: (delta: string) => void;
  onCompleted?: (payload: unknown) => void;
  onError?: (error: Error) => void;
  onArtifacts?: (result: CadDrawingGenerationResult) => void;
};

export async function listCadDrawingProjects(): Promise<CadDrawingProject[]> {
  const res = await authFetch(`${DRAWING_PREFIX}/projects`, { headers: jsonHeaders() });
  const data = await unwrap<{ projects: CadDrawingProject[]; count: number }>(res);
  return data.projects ?? [];
}

export async function createCadDrawingProject(name: string): Promise<CadDrawingProject> {
  const res = await authFetch(`${DRAWING_PREFIX}/projects`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });
  const data = await unwrap<{ project: CadDrawingProject }>(res);
  return data.project;
}

export async function renameCadDrawingProject(
  projectId: string,
  name: string,
): Promise<CadDrawingProject> {
  const res = await authFetch(`${DRAWING_PREFIX}/projects/${encodeURIComponent(projectId)}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });
  const data = await unwrap<{ project: CadDrawingProject }>(res);
  return data.project;
}

export async function deleteCadDrawingProject(projectId: string): Promise<void> {
  const res = await authFetch(`${DRAWING_PREFIX}/projects/${encodeURIComponent(projectId)}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  await unwrap<{ deleted: boolean }>(res);
}

export async function listCadDrawingFolders(projectId: string): Promise<CadDrawingFolder[]> {
  const res = await authFetch(
    `${DRAWING_PREFIX}/projects/${encodeURIComponent(projectId)}/folders`,
    { headers: jsonHeaders() },
  );
  const data = await unwrap<{ folders: CadDrawingFolder[]; count: number }>(res);
  return data.folders ?? [];
}

export async function createCadDrawingFolder(
  projectId: string,
  name: string,
  parentFolderId?: string | null,
): Promise<CadDrawingFolder> {
  const res = await authFetch(
    `${DRAWING_PREFIX}/projects/${encodeURIComponent(projectId)}/folders`,
    {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ name, parent_folder_id: parentFolderId ?? null }),
    },
  );
  const data = await unwrap<{ folder: CadDrawingFolder }>(res);
  return data.folder;
}

export async function renameCadDrawingFolder(
  folderId: string,
  name: string,
): Promise<CadDrawingFolder> {
  const res = await authFetch(`${DRAWING_PREFIX}/folders/${encodeURIComponent(folderId)}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });
  const data = await unwrap<{ folder: CadDrawingFolder }>(res);
  return data.folder;
}

export async function deleteCadDrawingFolder(folderId: string): Promise<void> {
  const res = await authFetch(`${DRAWING_PREFIX}/folders/${encodeURIComponent(folderId)}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  await unwrap<{ deleted: boolean }>(res);
}

export async function listCadDrawingFiles(
  projectId: string,
  options: CadDrawingFileListOptions = {},
): Promise<WorkspaceFile[]> {
  const params = new URLSearchParams();
  if (options.folderId) params.set('folder_id', options.folderId);
  if (options.includeAllFolders) params.set('include_all_folders', 'true');
  const qs = params.toString();
  const res = await authFetch(
    `${DRAWING_PREFIX}/projects/${encodeURIComponent(projectId)}/files${qs ? `?${qs}` : ''}`,
    { headers: jsonHeaders() },
  );
  const data = await unwrap<{ files: WorkspaceFile[]; count: number }>(res);
  return data.files ?? [];
}

export async function uploadCadDrawingFiles(
  projectId: string,
  files: File[],
  folderId?: string | null,
): Promise<CadDrawingUploadResponse> {
  const form = new FormData();
  for (const file of files) form.append('files', file, file.name);
  if (folderId) form.append('folder_id', folderId);
  const res = await authFetch(
    `${DRAWING_PREFIX}/projects/${encodeURIComponent(projectId)}/files`,
    { method: 'POST', body: form },
  );
  return unwrap<CadDrawingUploadResponse>(res);
}

export async function deleteCadDrawingFile(fileId: string): Promise<void> {
  const res = await authFetch(`${DRAWING_PREFIX}/files/${encodeURIComponent(fileId)}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  await unwrap<{ deleted: boolean }>(res);
}

export async function listCadDrawingMessages(
  projectId: string,
): Promise<CadDrawingMessage[]> {
  const res = await authFetch(
    `${DRAWING_PREFIX}/projects/${encodeURIComponent(projectId)}/messages`,
    { headers: jsonHeaders() },
  );
  const data = await unwrap<CadDrawingMessagesResponse>(res);
  return data.messages ?? [];
}

function extractSseJson(raw: string): unknown | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const data = trimmed
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trim())
    .join('\n');
  if (!data || data === '[DONE]') return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

function getDelta(frame: unknown): string {
  if (!frame || typeof frame !== 'object') return '';
  const obj = frame as Record<string, unknown>;
  const data = obj.data;
  if (typeof obj.delta === 'string') return obj.delta;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    if (typeof d.delta === 'string') return d.delta;
    if (typeof d.text === 'string') return d.text;
  }
  return '';
}

export async function streamCadDrawingChat(
  projectId: string,
  content: string,
  handlers: CadDrawingChatFrameHandlers = {},
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  const res = await authFetch(
    `${DRAWING_PREFIX}/projects/${encodeURIComponent(projectId)}/chat/stream`,
    {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ content }),
      signal: options.signal,
    },
  );
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${text}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split('\n\n');
    buffer = chunks.pop() ?? '';
    for (const chunk of chunks) {
      const frame = extractSseJson(chunk);
      if (!frame || typeof frame !== 'object') continue;
      const obj = frame as Record<string, unknown>;
      const status = obj.status;
      if (status === 'failed') {
        const message = String(obj.error_message || obj.error || '도면 생성 실패');
        const err = new Error(message);
        handlers.onError?.(err);
        throw err;
      }
      const data = obj.data;
      if (data && typeof data === 'object') {
        const d = data as Record<string, unknown>;
        if (d.event === 'cad_drawing.generated') {
          handlers.onArtifacts?.(d.detail as CadDrawingGenerationResult);
          continue;
        }
      }
      const delta = getDelta(frame);
      if (delta) handlers.onTextDelta?.(delta);
      if (status === 'completed') handlers.onCompleted?.(frame);
    }
  }
}

export { extractCompletedContent };
